import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown, User } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { BridgeMark, BrandWordmark } from "./BridgeMotif";
import { Button } from "./ui/button";

export function Navbar() {
  const { t, lang, changeLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home"), testId: "nav-link-home", end: true },
    { to: "/encontrar-apoyo", label: t("nav.find"), testId: "nav-link-find-support" },
    { to: "/iniciativas", label: t("nav.initiatives"), testId: "nav-link-initiatives" },
    { to: "/oportunidades", label: t("nav.opportunities"), testId: "nav-link-opportunities" },
  ];
  if (user && (user.role === "client" || user.role === "psychologist")) {
    links.push({ to: "/mis-citas", label: t("booking.my_appts"), testId: "nav-link-citas" });
  }

  const accountPath =
    user && user.role === "admin" ? "/admin"
    : user && user.role === "organizer" ? "/organizador"
    : user && user.role === "psychologist" ? "/mi-espacio"
    : user && user.role === "client" ? "/mi-cuenta"
    : "/login";

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const LangToggle = ({ scope = "" }) => (
    <div className="flex items-center rounded-full border border-border bg-white text-xs font-semibold overflow-hidden">
      {["es", "en", "am"].map((l) => (
        <button
          key={l}
          data-testid={`language-toggle-${l}${scope}`}
          onClick={() => { changeLang(l); if (user) api.patch("/auth/language", { lang: l }).catch(() => {}); }}
          className={`px-2.5 py-1 transition-colors ${l === "am" ? "" : "uppercase"} ${lang === l ? "bg-slateink text-white" : "text-muted-foreground hover:text-slateink"}`}
        >
          {l === "am" ? "አማርኛ" : l}
        </button>
      ))}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-cream/85 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <BridgeMark size={30} />
          <BrandWordmark className="text-xl" />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "text-terracotta" : "text-slateink/80 hover:text-slateink hover:bg-secondary"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <LangToggle />
          {user ? (
            <div className="flex items-center gap-2">
              <Button asChild variant="ghost" size="sm" data-testid="nav-account-btn">
                <Link to={accountPath} className="flex items-center gap-1.5">
                  <User className="h-4 w-4" /> {t("nav.account")}
                </Link>
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="nav-logout-btn">
                {t("nav.logout")}
              </Button>
            </div>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" data-testid="nav-link-login">
                <Link to="/login">{t("nav.login")}</Link>
              </Button>
              <Button asChild size="sm" className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="nav-link-join-psychologist">
                <Link to="/unirme">{t("nav.join")}</Link>
              </Button>
            </>
          )}
        </div>

        <button
          className="flex items-center lg:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6 text-slateink" /> : <Menu className="h-6 w-6 text-slateink" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-border/60 bg-cream lg:hidden" data-testid="mobile-menu">
          <div className="space-y-1 px-4 py-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${l.testId}`}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2.5 text-sm font-medium ${isActive ? "bg-terracotta-light text-terracotta" : "text-slateink"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-3">
              <LangToggle scope="-mobile" />
            </div>
            <div className="flex flex-col gap-2 pt-3">
              {user ? (
                <>
                  <Button asChild variant="outline" onClick={() => setOpen(false)}>
                    <Link to={accountPath}>{t("nav.account")}</Link>
                  </Button>
                  <Button variant="ghost" onClick={handleLogout} data-testid="mobile-logout-btn">{t("nav.logout")}</Button>
                </>
              ) : (
                <>
                  <Button asChild variant="outline" onClick={() => setOpen(false)}>
                    <Link to="/login">{t("nav.login")}</Link>
                  </Button>
                  <Button asChild className="bg-terracotta hover:bg-terracotta-hover" onClick={() => setOpen(false)}>
                    <Link to="/unirme">{t("nav.join")}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
